import { BehaviorSubject, Subject } from "rxjs"
import {
  ImportMediaDocument,
} from "../graphql/types"
import type {
  ImportMediaMutation,
  ImportMediaMutationVariables,
} from "../graphql/types"
import { UploadPhase } from "./Uploads"
import type { Upload, UploadState } from "./Uploads"
import type { ApolloClient } from "@apollo/client/core"
import type { NormalizedCacheObject } from "@apollo/client/core"
import { notifyErrors } from "../notifications"
import { createLogger } from "../logger"
import type { LoggerContext } from "../logger"
import { serializeError } from "serialize-error"
import type { Logger } from "roarr"
import type { MediaLinkContext } from "./mediaLinkContext"

export class ImportImpl implements Upload {
  get name(): string {
    return this.url.pathname.split("/").pop() ?? this.url.pathname ?? this.url.href
  }

  public size = 0
  readonly state: Subject<UploadState>
  private logger: Logger<LoggerContext>

  constructor(
    public url: URL,
    private graphql: ApolloClient<NormalizedCacheObject>,
    private links: MediaLinkContext
  ) {
    this.logger = createLogger(`ImportImpl[${url}]`)

    this.logger.debug(
      `Starting import ${url}`
    )

    this.state = new BehaviorSubject<UploadState>({
      phase: UploadPhase.PREPARING
    })

    this.state.next({
      phase: UploadPhase.PREPARING,
      progress: 0,
      bytes: 0
    })

    this.state.next({
      phase: UploadPhase.UPLOADING,
      progress: -1,
      bytes: 0
    })

    const variables: ImportMediaMutationVariables = {
      url: this.url.href,
      games: this.links.games,
      gameSeries: this.links.gameSeries,
      gameConsoles: this.links.gameConsoles,
      companies: this.links.companies,
      tags: this.links.tags,
    }

    this.graphql
      .mutate<ImportMediaMutation, ImportMediaMutationVariables>({
        mutation: ImportMediaDocument,
        variables
      })
      .then((query) => {
        if (query.errors?.length) {
          notifyErrors(query.errors)
          this.state.next({
            phase: UploadPhase.FAILED,
            error: query.errors?.[0]?.message ?? 'Unknown error'
          })
        }

        return query.data?.importMedia
      })
      .then((media) => {
        if (media) {
          this.logger.debug(`Import completed`)

          this.state.next({
            phase: UploadPhase.COMPLETE,
            progress: 1,
            media
          })
        }
      })
      .catch((error) => {
        this.logger.error({ error: serializeError(error) }, "Import failed")

        this.state.next({
          phase: UploadPhase.FAILED,
          error
        })
      })
  }
}
